import { DownOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu } from 'antd';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import LogoutIconActive from 'src/assets/imgs/logout-icon-active.svg';
import LogoutIcon from 'src/assets/imgs/logout-icon.svg';
import { RoutePaths } from 'src/core/router/RoutePaths';
import { Router } from 'src/core/router/Router';
import { AuthStore } from 'src/stores/AuthStore/AuthStore';
import { ProcessDefinitionsStore } from 'src/stores/ProcessDefinitionsStore/ProcessDefinitionsStore';
import { UrlHelper } from 'src/utils/UrlHelper';
import styled from 'styled-components';

/* ═════════════════════════════════  MAIN COMPONENT  ═══════════════════════ */

export const UserMenu = observer(() => {
    const [visible, setVisible] = useState(false);
    const closeMenu = () => setVisible(false);

    return (
        <StyledDropdown
            placement="bottomRight"
            arrow
            trigger={['click']}
            overlay={<UserMenuContent closeMenu={closeMenu} />}
            onVisibleChange={setVisible}
            visible={visible}
            /* avoid header overflow clipping */
            getPopupContainer={() => document.body}
        >
            <StyledButton>
                <StyledAvatar>
                    <UserOutlined />
                </StyledAvatar>

                <StyledIconContainerDropdown>
                    <DownOutlined />
                </StyledIconContainerDropdown>
            </StyledButton>
        </StyledDropdown>
    );
});

/* ═════════════════════════════════  MENU CONTENT  ════════════════════════ */

type ContentProps = { closeMenu: () => void };

const UserMenuContent = observer(({ closeMenu }: ContentProps) => (
    <StyledMenu
        onClick={(e) => {
            e.domEvent.preventDefault();
            closeMenu();
        }}
    >
        {ProcessDefinitionsStore.accountManagementProcesses?.map((process) => (
            <MenuItem
                key={process.key}
                data-testid={`app-header-user-menu-${process.key}`}
                onClick={() => {
                    Router.navigate(
                        UrlHelper.getTargetUrl(RoutePaths.process, {
                            processKey: process.key,
                        }),
                    );
                }}
            >
                <Trans i18nKey={`${process.extensions?.l10nTitleKey}`} />
            </MenuItem>
        ))}

        <MenuItem
            onClick={() => {
                Router.navigate(RoutePaths.profile);
            }}
            data-testid="app-header-user-menu-profile"
        >
            <Trans i18nKey="App.Header.Menu.Profile" />
        </MenuItem>

        <MenuItem
            onClick={() => {
                AuthStore.logout();
            }}
            data-testid="app-header-user-menu-logout"
        >
            <IconContainer className="icon-container">
                <img alt="logout" src={LogoutIcon} />
            </IconContainer>
            <IconContainer className="icon-container-active">
                <img alt="logout" src={LogoutIconActive} />
            </IconContainer>
            <Trans i18nKey="App.Header.Menu.LogOut" />
        </MenuItem>
    </StyledMenu>
));

/* ════════════════════════════════  STYLES  ═══════════════════════════════ */

const StyledDropdown = styled(Dropdown)`
    div {
        .ant-dropdown-arrow {
            background: var(--header-background) !important;
        }
    }
`;

/* identical trigger metrics/paddings as LanguageSwitcher */
const StyledButton = styled(Button)`
    height: 29px;
    background: var(--header-background);
    display: flex;
    align-items: center;
    font-size: 16px;
    border: none;
    margin: 0 20px;
    padding: 0 8px;
    cursor: pointer;

    span {
        color: var(--header-text-color);
    }
    svg {
        fill: var(--header-text-color);
    }

    &:focus,
    &:hover {
        color: #0d0f1c;
    }
`;

const StyledAvatar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background: #f6f7f8;
    color: #8b959e;
    font-weight: 700;
    margin-right: 6px;
`;

const StyledIconContainerDropdown = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--header-background);
    font-size: 10px;
`;

/* menu shell copied from LanguageSwitcher */
const StyledMenu = styled(Menu)`
    border: none;
    border-radius: 4px;
    box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
        0 9px 28px 8px rgb(0 0 0 / 5%) !important;
    padding: 11px;

    .ant-menu-item {
        color: var(--header-text-color);

        &:after {
            display: none;
        }
        + .ant-menu-item {
            padding: 4px 19px;
        }
        &:hover {
            color: #000;
        }
        &.ant-menu-item-selected,
        &:active {
            background-color: transparent;
        }
    }
`;

const MenuItem = styled.div`
    width: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: 15px;
    height: 30px;
    background: var(--header-background);
    color: var(--header-text-color);
    padding: 10px 19px 10px 10px;
    cursor: pointer;

    &:hover {
        color: #000;
        .icon-container {
            display: none;
        }
        .icon-container-active {
            display: block;
        }
    }
`;

/* icon tricks reproduced from the original code */
const IconContainer = styled.div`
    margin-right: 7px;

    &.icon-container-active {
        display: none;
    }
`;
